import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { useToggle } from "@uidotdev/usehooks";
import { cn } from "cn";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { BwCheckmarkLine, BwChevronDownLine } from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { formatEnumLabel } from "@/libs/format-enum-label";

export const ACQUISITION_CHANNEL_VALUES = [
  "EMAIL",
  "BLOG",
  "SOCIAL_MEDIA",
  "ALT_MEDIA",
  "EVENT",
  "DISPLAY_ADVERTISING",
  "REFERRAL",
  "PAID_SEARCH",
  "ORGANIC_SEARCH",
  "PARTNER",
] as const;

type AcquisitionChannelSelectProps = FieldWrapperInjectedProps & {
  /** `""` = None. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  "data-test"?: string;
};

export default function AcquisitionChannelSelect({
  className,
  value,
  onChange,
  disabled,
  defaultOpen = false,
  onOpenChange,
  ...props
}: AcquisitionChannelSelectProps) {
  const [open, toggleOpen] = useToggle(defaultOpen);

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open);
    toggleOpen(open);
  };

  return (
    <Popover modal={true} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger disabled={disabled} asChild>
        <div
          role="combobox"
          className={cn(
            /* sorted */
            "flex",
            "items-center justify-between",
            "w-full h-10",
            "px-3 py-2",
            "border border-border-dark-low rounded-lg",
            "aria-invalid:border-border-negative-max aria-invalid:data-[state=open]:border-border-negative-max data-[state=open]:border-border-dark-max data-[state=open]:border-[1.5px]",
            !value && "text-text-dark-min",
            className,
          )}
          {...props}
        >
          {value ? (
            <p className="text-text-dark-high text-sm font-medium">
              {formatEnumLabel(value)}
            </p>
          ) : (
            "Select channel"
          )}
          <BwChevronDownLine
            size={16}
            className="text-text-dark-high shrink-0"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command>
          <CommandList>
            <CommandGroup>
              <CommandItem
                value=""
                onSelect={() => {
                  onChange("");
                  handleOpenChange(false);
                }}
              >
                <p className="grow text-text-dark-low">None</p>
                <BwCheckmarkLine
                  size={16}
                  className={cn(
                    /* sorted */
                    "shrink-0",
                    !value ? "opacity-100" : "opacity-0",
                  )}
                />
              </CommandItem>
              {ACQUISITION_CHANNEL_VALUES.map((option) => (
                <CommandItem
                  value={option}
                  key={option}
                  onSelect={(e) => {
                    onChange(e);
                    handleOpenChange(false);
                  }}
                >
                  <p className="grow">{formatEnumLabel(option)}</p>
                  <BwCheckmarkLine
                    size={16}
                    className={cn(
                      /* sorted */
                      "shrink-0",
                      value === option ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
